import type { Extensions, JSONContent } from '@tiptap/core';
import type { Mark, Node } from '@tiptap/pm/model';

// Server-safe schema set (no React) replaces the editor's CommonExtensions +
// CustomComponentsExtensions + CommentMarkStub + TemplatePlaceholderExtension.
// ServerRenderExtensions already bundles CommentMarkStub and the
// template-placeholder schema, so callers get identical schema coverage.
import { ServerRenderExtensions } from './schema';
import type { TiptapStaticRendererOptions } from '../markdown/static-renderer/json/renderer.js';
import { renderToHTMLString, serializeChildrenToHTMLString } from '../markdown/static-renderer/pm/html-string/index.js';
import { encodeBase64 } from './base64';
import { gitHubEmojis } from '@tiptap/extension-emoji';

const SUPPORTED_CODE_LANGUAGES = new Set([
  'astro',
  'bash',
  'batch',
  'clojure',
  'c',
  'cpp',
  'csharp',
  'css',
  'dart',
  'diff',
  'dockerfile',
  'elixir',
  'erlang',
  'go',
  'graphql',
  'groovy',
  'haskell',
  'hcl',
  'html',
  'ini',
  'java',
  'javascript',
  'json',
  'jsonc',
  'kotlin',
  'latex',
  'less',
  'liquid',
  'lua',
  'makefile',
  'markdown',
  'mdx',
  'nginx',
  'objectivec',
  'ocaml',
  'perl',
  'php',
  'powershell',
  'python',
  'r',
  'regex',
  'ruby',
  'rust',
  'scala',
  'scss',
  'shell',
  'sql',
  'svelte',
  'swift',
  'terraform',
  'text',
  'toml',
  'tsx',
  'typescript',
  'vb',
  'vbnet',
  'vue',
  'wasm',
  'xml',
  'yaml',
]);

const CODE_LANGUAGE_ALIASES = new Map<string, string>([
  ['plain', 'text'],
  ['plaintext', 'text'],
  ['textmate', 'text'],
  ['sh', 'bash'],
  ['shell', 'bash'],
  ['shellsession', 'bash'],
  ['bashsession', 'bash'],
  ['zsh', 'bash'],
  ['terminal', 'bash'],
  ['console', 'bash'],
  ['cmd', 'batch'],
  ['dos', 'batch'],
  ['c++', 'cpp'],
  ['cc', 'cpp'],
  ['hpp', 'cpp'],
  ['c#', 'csharp'],
  ['cs', 'csharp'],
  ['objc', 'objectivec'],
  ['objective-c', 'objectivec'],
  ['objectivec', 'objectivec'],
  ['js', 'javascript'],
  ['jsx', 'javascript'],
  ['mjs', 'javascript'],
  ['cjs', 'javascript'],
  ['ts', 'typescript'],
  ['tsx', 'tsx'],
  ['py', 'python'],
  ['rb', 'ruby'],
  ['rs', 'rust'],
  ['json5', 'json'],
  ['yml', 'yaml'],
  ['tf', 'terraform'],
  ['hcl', 'terraform'],
  ['md', 'markdown'],
  ['mdown', 'markdown'],
  ['ps1', 'powershell'],
  ['pwsh', 'powershell'],
  ['docker', 'dockerfile'],
]);

export function normalizeCodeBlockLanguage(language?: string | null): string {
  if (!language || typeof language !== 'string') {
    return 'text';
  }

  const trimmed = language.trim();
  if (!trimmed) {
    return 'text';
  }

  const lowered = trimmed.toLowerCase();
  const alias = CODE_LANGUAGE_ALIASES.get(lowered) ?? lowered;

  if (SUPPORTED_CODE_LANGUAGES.has(alias)) {
    return alias;
  }

  return 'text';
}

function esc(val: any) {
  const jsonStr = JSON.stringify(val);
  const encoded = encodeBase64(jsonStr);
  return `"${encoded}"`;
}

function serializeActionPillParams(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '{}') {
      return null;
    }

    if (trimmed.startsWith('base64:')) {
      return `params="${trimmed}"`;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return `params="base64:${encodeBase64(JSON.stringify(parsed))}"`;
    } catch {
      return serializeAttr('params', value);
    }
  }

  if (typeof value === 'object') {
    return `params="base64:${encodeBase64(JSON.stringify(value))}"`;
  }

  return serializeAttr('params', value);
}

function indentNestedLists(rawContent: string) {
  // Accumulate indentation for deeper list levels by prepending four spaces to any list marker line
  return rawContent.replace(/\n( *)([*-]|\d+\.)\s/g, (_match, spaces, marker) => `\n${spaces}    ${marker} `);
}

function serializeAttr(key: string, value: any): string {
  // Debug logging for iframe attributes
  if (key === 'width' || key === 'height' || key === 'allowfullScreen' || key === 'autoplay' ||
      key === 'endTime' || key === 'ivLoadPolicy' || key === 'start' || key === 'rel') {
    // console.log(`[serializeAttr] Processing iframe attribute: ${key}=${JSON.stringify(value)} (type: ${typeof value})`);
  }

  // Special handling for checkbox attributes produced by Task List items
  // 1. <li data-checked="false">  → we want quoted string for data attributes
  // 2. <input checked=null />       → convert null/undefined to {false}
  if (key === 'checked' && (value === null || value === undefined)) {
    return `${key}={false}`;
  }

  if (value === true) {
    // For iframe boolean attributes, use quoted string to avoid MDX parsing issues
    const iframeBooleanAttributes = [
      'allowfullScreen', 'autoplay', 'loop', 'controls',
      'disableKBcontrols', 'enableIFrameApi', 'modestBranding',
      'muted', 'playsinline'
    ];
    if (iframeBooleanAttributes.includes(key)) {
      // console.log(`[serializeAttr] Quoting boolean iframe attribute: ${key}="true"`);
      return `${key}="true"`;
    }
    return key; // Boolean true becomes just the key name
  }
  if (value === false) {
    // For data-* attributes output quoted string to avoid JSX expression syntax
    if (key.startsWith('data-')) {
      return `${key}="false"`;
    }
    return `${key}={false}`;
  }
  if (value === null) {
    // Preserve quoted string for data attributes, otherwise {null}
    if (key.startsWith('data-')) {
      return `${key}="null"`;
    }
    return `${key}={null}`;
  }
  if (value === undefined) {
    // console.warn(`[serializeAttr] WARNING: Attribute "${key}" has undefined value`);
    console.trace(); // 打印调用栈
    return `${key}={undefined}`;
  }
  if (typeof value === 'number') {
    // Numeric attributes cause MDX build errors when written as JSX expressions.
    // Emit them as plain strings instead for all common numeric attributes.
    const numericAttributesThatNeedQuotes = [
      'limit', 'maxSize', 'width', 'height', 'size',
      'count', 'max', 'min', 'step', 'rows', 'cols',
      'span', 'tabIndex', 'zIndex', 'order', 'flexGrow',
      'flexShrink', 'opacity', 'lineHeight', 'fontSize',
      'number', 'screenshotWidth', 'screenshotHeight',
      'screenshotZoomLevel',
      // iframe specific attributes
      'frameBorder', 'marginWidth', 'marginHeight',
      'scrolling', 'vspace', 'hspace', 'border',
      // YouTube iframe attributes
      'endTime', 'ivLoadPolicy', 'start', 'rel',
      'progressBarColor', 'ccLoadPolicy'
    ];

    if (numericAttributesThatNeedQuotes.includes(key)) {
      // console.log(`[serializeAttr] Quoting numeric attribute: ${key}="${value}"`);
      return `${key}="${value}"`;
    }
    // console.log(`[serializeAttr] NOT quoting numeric attribute: ${key}={${value}}`);
    return `${key}={${value}}`;
  }
  // List of attributes that should be base64 encoded
  const encodedAttributes = [
    'title', 'guideAction', 'interactionPoints', 'elementBox'
  ];
  
  // For complex objects (arrays and objects), always base64 encode
  if (typeof value === 'object' && value !== null) {
    return `${key}=${esc(value)} data-${key}-encoded="base64"`;
  }
  
  // For specific attributes that should be base64 encoded
  if (encodedAttributes.includes(key)) {
    return `${key}=${esc(value)} data-${key}-encoded="base64"`;
  }
  
  // String values containing quotes or braces need base64 encoding to avoid breaking MDX attributes
  if (typeof value === 'string' && (value.includes('"') || (value.startsWith('{') && value.length > 2))) {
    return `${key}=${esc(value)} data-${key}-encoded="base64"`;
  }

  // For everything else, keep as plain string
  return `${key}="${value}"`;
}

function transformCustomAttrs(attrs: any): any {
  const processedAttrs: any = {};
  // console.log('[transformCustomAttrs] Input attrs:', JSON.stringify(attrs));
  Object.entries(attrs).forEach(([key, value]) => {
    // console.log(`[transformCustomAttrs] Processing: ${key} = ${JSON.stringify(value)}`);
    if (value !== undefined && value !== null) {
      // Always preserve the original attribute
      processedAttrs[key] = value;
      
      // Special handling for data-dg-id - keep it as a plain string
      if (key === 'id') {
        processedAttrs['data-dg-id'] = value;
      }
      // Add data-dg-* version for other tracking attributes
      else if (['color', 'priority', 'theme', 'style'].includes(key)) {
        processedAttrs[`data-dg-${key}`] = value;
      }
    } else {
      // console.log(`[transformCustomAttrs] Skipping ${key} because value is ${value}`);
    }
  });
  // console.log('[transformCustomAttrs] Output attrs:', JSON.stringify(processedAttrs));
  return processedAttrs;
}

/**
 * Renders TipTap content to clean, human-readable markdown without MDX components.
 * Focuses on text hierarchy and readability rather than bidirectional fidelity.
 */
export function renderToMarkdownReadable(content: Node | JSONContent,
  extensions: Extensions = [],
  options: Partial<TiptapStaticRendererOptions<string, Mark, Node>> = {}
) {
  if (!content) {
    return ''
  }
  
  // Handle empty array content
  if (Array.isArray(content) && content.length === 0) {
    return ''
  }
  
  // Handle empty doc
  if (typeof content === 'object' && content.type === 'doc' && 
      (!content.content || (Array.isArray(content.content) && content.content.length === 0))) {
    return ''
  }
  
  if ((!content.type || content.type === 'doc') && Array.isArray(content.content)) {
    let markdown = ''
    for (const child of (content.content ?? [])) {
      markdown += renderToMarkdownReadable(child, extensions, options)
    }
    return markdown
  }
  const htmlResult = renderToHTMLString({
    content,
    extensions: [
      ...ServerRenderExtensions,
      ...extensions
    ] as any,
    options: {
      nodeMapping: {
        // Remove custom text handler to allow marks to be processed
        bulletList({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}`
        },
        orderedList({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}`
        },
        // Handle Confluence specific ri:url nodes by converting to regular links
        "ri:url"({ node, children }) {
          const href = node.attrs?.riValue || node.attrs?.url || ''
          const text = serializeChildrenToHTMLString(children) || href
          return href ? `[${text}](${href})` : text
        },

        listItem({ node, children, parent }) {
          const rawContent = serializeChildrenToHTMLString(children).trim();
          const childrenContent = indentNestedLists(rawContent);
          if (parent?.type.name === 'bulletList') {
            return `- ${childrenContent}\n`
          }
          if (parent?.type.name === 'orderedList') {
            let number = parent.attrs.start || 1
            parent.forEach((parentChild, _offset, index) => {
              if (node === parentChild) {
                number = index + 1
              }
            })
            return `${number}. ${childrenContent}\n`
          }
          return serializeChildrenToHTMLString(children)
        },
        paragraph({ children, parent }) {
          const content = serializeChildrenToHTMLString(children);
          // Inside list items, avoid extra blank lines; listItem handles spacing
          if (parent?.type.name === 'listItem') {
            return content;
          }
          return `\n${content}\n`
        },
        heading({ node, children }) {
          const level = node.attrs.level as number
          const content = serializeChildrenToHTMLString(children).trim();
          return `\n${new Array(level).fill('#').join('')} ${content}\n\n`
        },
        codeBlock({ node, children }) {
          const language = normalizeCodeBlockLanguage(node.attrs?.language);
          return `\n\`\`\`${language}\n${serializeChildrenToHTMLString(children)}\n\`\`\`\n`
        },
        blockquote({ children }) {
          return `\n${serializeChildrenToHTMLString(children)
            .trim()
            .split('\n')
            .map(a => `> ${a}`)
            .join('\n')}\n`
        },
        image({ node }) {
          return `![${node.attrs.alt || ''}](${node.attrs.src})`
        },
        hardBreak() {
          return '\n'
        },
        horizontalRule() {
          return '\n---\n'
        },
        templatePlaceholder({ node }) {
          return `{{${node.attrs?.name || 'PLACEHOLDER'}}}`
        },
        table({ children, node }) {
          // Ensure GitHub-flavored Markdown table with header separator
          if (!Array.isArray(children)) {
            return `\n${serializeChildrenToHTMLString(children)}\n`
          }

          // Determine number of columns from the first table row
          const firstRowNode = (node as any)?.firstChild;
          const numCols = firstRowNode ? firstRowNode.childCount : Math.max(1, Array.isArray(children[0]) ? (children[0] as any[]).length : String(children[0] || '').split('|').length - 1);

          const header = serializeChildrenToHTMLString(children[0]);
          const separator = `| ${Array.from({ length: numCols }).map(() => '---').join(' | ')} |`;
          const body = serializeChildrenToHTMLString(children.slice(1));

          return `\n${header}${separator}\n${body}\n`;
        },
        tableRow({ children }) {
          if (Array.isArray(children)) {
            return `| ${children.join(' | ')} |\n`
          }
          return `${serializeChildrenToHTMLString(children)}\n`
        },
        thead({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        tableHeader({ children }) {
          return serializeChildrenToHTMLString(children).trim()
        },
        tableCell({ children }) {
          return serializeChildrenToHTMLString(children).trim()
        },
        
        // Custom components - render as readable text sections
        Card({ node, children }) {
          const title = node.attrs?.title || 'Card'
          const href = node.attrs?.href
          const callToAction = node.attrs?.callToAction
          const content = serializeChildrenToHTMLString(children).trim()
          
          let cardContent = `\n## ${title}\n\n`
          
          
          cardContent += `${content}\n\n`
          
          // Add call-to-action link if present
          if (href && callToAction) {
            cardContent += `[${callToAction}](${href})\n\n`
          } else if (href) {
            cardContent += `[Learn More](${href})\n\n`
          }
          
          return cardContent
        },
        Step({ node, children }) {
          const title = node.attrs?.title || node.attrs?.guideAction || 'Step'
          const number = node.attrs?.number || ''
          const content = serializeChildrenToHTMLString(children).trim()
          const screenshotUrl = node.attrs?.screenshotUrl || ''
          // NOTE: ignore cropStyle for now
          // const cropStyle = node.attrs?.cropStyle || 'cropped'
          
          let stepContent = `\n### ${number ? `${number}. ` : ''}${title}\n\n`
          
          
          stepContent += `${content}\n\n`
          
          // Add screenshot if cropStyle is not 'none' and URL exists
          if (screenshotUrl) {
            stepContent += `![Screenshot for ${title}](${screenshotUrl})\n\n`
          }
          
          return stepContent
        },
        Tab({ node, children }) {
          const title = node.attrs?.title || 'Tab'
          const content = serializeChildrenToHTMLString(children).trim()
          return `\n### ${title}\n\n${content}\n\n`
        },
        Tabs({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}\n`
        },
        // Internal notes are editor-only and should not be published
        InternalNote() {
          return ''
        },
        Screenshot({ node }) {
          // Note: Screenshot is deprecated - screenshots are now integrated into Step elements via cropStyle
          // This handler is kept for backward compatibility with existing content
          const alt = node.attrs?.alt || node.attrs?.caption || 'Screenshot'
          const url = node.attrs?.url || ''
          
          let screenshotContent = ''
          
          if (url) {
            screenshotContent += `\n![${alt}](${url})\n\n`
          } else {
            screenshotContent += `\n*[${alt}]*\n\n`
          }
          
          return screenshotContent
        },
        Admonition({ node, children }) {
          const type = node.attrs?.admonitionType || 'note'
          const title = node.attrs?.title || type.charAt(0).toUpperCase() + type.slice(1)
          const content = serializeChildrenToHTMLString(children).trim()
          return `\n> **${title}**\n> \n> ${content.split('\n').join('\n> ')}\n\n`
        },
        Accordion({ node, children }) {
          const title = node.attrs?.title || 'Accordion'
          const content = serializeChildrenToHTMLString(children).trim()
          return `\n### ${title}\n\n${content}\n\n`
        },
        AccordionGroup({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}\n`
        },
        Citation({ node, children }) {
          const content = serializeChildrenToHTMLString(children).trim()
          const title = node.attrs?.title
          const slug = node.attrs?.slug
          const sourceType = node.attrs?.sourceType
          const isBrokenLink = node.attrs?.isBrokenLink
          
          let citationContent = `*${content}*`
          
          // Add citation metadata
          if (title || slug || sourceType) {
            citationContent += ' ('
            const metadata = []
            if (title) metadata.push(`Title: ${title}`)
            if (sourceType) metadata.push(`Source: ${sourceType}`)
            if (slug) metadata.push(`Ref: ${slug}`)
            if (isBrokenLink) metadata.push('⚠️ Broken Link')
            citationContent += metadata.join(', ')
            citationContent += ')'
          }
          
          return citationContent
        },
        Glossary({ node, children }) {
          const content = serializeChildrenToHTMLString(children).trim()
          const term = node.attrs?.term
          const definition = node.attrs?.definition
          const slug = node.attrs?.slug
          
          let glossaryContent = `**${content}**`
          
          // Add glossary metadata
          if (term || definition || slug) {
            glossaryContent += ' ('
            const metadata = []
            if (term) metadata.push(`Term: ${term}`)
            if (definition) metadata.push(`Definition: ${definition}`)
            if (slug) metadata.push(`Ref: ${slug}`)
            glossaryContent += metadata.join(', ')
            glossaryContent += ')'
          }
          
          return glossaryContent
        },
        imageUpload({ node }) {
          const attrs = node.attrs || {};
          const src = attrs.src || attrs.url || '';
          const alt = attrs.alt || attrs.caption || 'Uploaded Image';
          const width = attrs.width;
          const height = attrs.height;
          
          if (src) {
            // Generate standard Markdown image syntax
            let imageMarkdown = `![${alt}](${src})`;
            
            // Optional: add dimension info as comment
            if (width || height) {
              imageMarkdown += `\n<!-- width: ${width || 'auto'}, height: ${height || 'auto'} -->`;
            }
            
            return `\n${imageMarkdown}\n\n`;
          }
          
          // Return placeholder when no URL
          return `\n*[Image placeholder: ${alt}]*\n\n`;
        },

        FolderContents({ node }) {
          // Try to get topicData from attributes
          const topicDataRaw = node.attrs?.topicData;
          
          if (topicDataRaw) {
            try {
              let topicData: any[];
              
              // Check if topicData is already an object (server-side rendering)
              if (typeof topicDataRaw === 'object' && Array.isArray(topicDataRaw)) {
                // Already decoded as an array
                topicData = topicDataRaw;
              } else if (typeof topicDataRaw === 'string') {
                // It's a base64 string, decode it
                let decodedString: string;
                if (typeof Buffer !== 'undefined') {
                  // Node.js environment
                  decodedString = Buffer.from(topicDataRaw, 'base64').toString('utf-8');
                } else if (typeof atob !== 'undefined') {
                  // Browser environment
                  decodedString = decodeURIComponent(escape(atob(topicDataRaw)));
                } else {
                  throw new Error('Cannot decode base64 in this environment');
                }
                topicData = JSON.parse(decodedString);
              } else {
                console.warn('topicData is neither array nor string:', typeof topicDataRaw);
                throw new Error('Invalid topicData format');
              }
              
              // Generate markdown list for each topic
              const items = topicData.map((topic: any) => {
                const title = topic.title || 'Untitled';
                const description = topic.description || '';
                const slug = topic.canonicalSlug || '';
                
                // Create markdown link
                const href = slug ? `/docs/${slug}` : '';
                
                if (href) {
                  return `- [${title}](${href})${description ? `: ${description}` : ''}`;
                }
                return `- **${title}**${description ? `: ${description}` : ''}`;
              }).join('\n');
              
              return `\n${items}\n\n`;
            } catch (error) {
              console.error('Failed to parse FolderContents topicData:', error);
            }
          }
          
          // Fallback to simple format if no topicData
          const href = node.attrs?.href || '';
          const title = node.attrs?.title || 'Document';
          const description = node.attrs?.description || '';
          const icon = node.attrs?.icon || '📄';
          
          if (href) {
            return `\n${icon} **[${title}](${href})**${description ? `\n   _${description}_` : ''}\n\n`;
          }
          
          return `\n${icon} **${title}**${description ? `\n   _${description}_` : ''}\n\n`;
        },
        CardGroup({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}\n`
        },
        toggle({ node, children }) {
          const title = node.attrs?.title || 'Toggle'
          const content = serializeChildrenToHTMLString(children).trim()
          return `\n### ${title}\n\n${content}\n\n`
        },
        Steps({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}\n`
        },
        mermaid({ node, children }) {
          // Get code from content text or attrs as fallback
          const codeFromContent = children ? serializeChildrenToHTMLString(children) : ''
          const codeFromAttrs = node.attrs?.code || ''
          const code = codeFromContent || codeFromAttrs

          // Return standard Markdown code block instead of MDX component
          return `\n\`\`\`mermaid\n${code}\n\`\`\`\n`
        },

        youtube({ node }) {
          const src = node.attrs?.src || '';

          if (!src) {
            return '\n[YouTube Video]\n\n';
          }

          // Extract video ID from URL
          let videoId = '';
          try {
            const url = new URL(src);
            if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
              if (url.hostname.includes('youtu.be')) {
                videoId = url.pathname.slice(1);
              } else {
                videoId = url.searchParams.get('v') || '';
              }
            }
          } catch {
            // If URL parsing fails, just use the src as-is
          }

          // Return a simple markdown link
          if (videoId) {
            return `\n[Watch Video](https://www.youtube.com/watch?v=${videoId})\n\n`;
          }

          return `\n[Watch Video](${src})\n\n`;
        },

        // Inline components - render as readable text
        inlineButton({ node }) {
          const isSingleWorkspacePortal = Boolean((options as any)?.singleWorkspacePortal);
          const { text, href } = node.attrs;
          let finalHref = href;
          try {
            if (href && href.startsWith('/') && !href.startsWith('//')) {
              const url = new URL(href, 'http://local');
              const segs = url.pathname.split('/').filter(Boolean);
              const wsIdx = segs.findIndex(s => s === 'workspaces');
              const docsIdx = segs.findIndex(s => s === 'docs');
              if (wsIdx >= 0 && docsIdx >= 0 && wsIdx + 1 < segs.length && docsIdx + 1 < segs.length && docsIdx > wsIdx) {
                const ws = segs[wsIdx + 1];
                const tail = segs.slice(docsIdx + 1).join('/');
                finalHref = isSingleWorkspacePortal ? `/docs/${tail}` : `/docs/${ws}/${tail}`;
              } else if (segs.length >= 2) {
                const ws = segs[0];
                const tail = segs.slice(1).join('/');
                finalHref = isSingleWorkspacePortal ? `/docs/${tail}` : `/docs/${ws}/${tail}`;
              }
            }
          } catch {}
          if (finalHref) {
            return `[${text || 'Button'}](${finalHref})`;
          }
          return `**${text || 'Button'}**`;
        },
        
        actionPill({ node }) {
          const { label, provider, action } = node.attrs;
          return `[${label || `${(provider || '').toUpperCase()}: ${action || ''}`}]`;
        },

        runbookRefMention({ node }) {
          const ref = node.attrs?.ref;
          return typeof ref === 'string' && ref.length > 0 ? ref : '';
        },

        inlineImage({ node }) {
          const { src, alt, title } = node.attrs;
          let result = `![${alt || ''}](${src || ''})`;
          if (title) {
            result = `![${alt || ''}](${src || ''} "${title}")`;
          }
          return result;
        },
        
        emoji({ node }) {
          const { name } = node.attrs;
          // Find the actual emoji character from TipTap's emoji data
          const emojiData = gitHubEmojis.find(e => e.name === name);
          if (emojiData && emojiData.emoji) {
            return emojiData.emoji;
          }
          // Fallback to emoji name in colons if not found
          return `:${name}:`;
        },
        
        // Column layout support
        columns({ node, children }) {
          const layout = node.attrs?.layout || 'two-column';
          // Add a descriptive comment
          return `\n<!-- Columns layout: ${layout} -->\n${serializeChildrenToHTMLString(children)}\n`;
        },

        column({ node, children }) {
          const position = node.attrs?.position || '';
          // Use horizontal line to separate column content
          return `\n${serializeChildrenToHTMLString(children)}${position !== 'last' ? '\n---\n' : '\n'}`;
        },
        
        // Task list support
        taskItem({ node, children }) {
          const checked = !!node.attrs?.checked;
          const content = serializeChildrenToHTMLString(children).trim();
          return `- [${checked ? 'x' : ' '}] ${content}\n`;
        },

        taskList({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}\n`;
        },
        
        ...options?.nodeMapping,
      },
      markMapping: {
        bold({ children }) {
          return `**${serializeChildrenToHTMLString(children)}**`
        },
        italic({ children }) {
          return `*${serializeChildrenToHTMLString(children)}*`
        },
        code({ children }) {
          return `\`${serializeChildrenToHTMLString(children)}\``
        },
        strike({ children }) {
          return `~~${serializeChildrenToHTMLString(children)}~~`
        },
        strong({ children }) {
          return `**${serializeChildrenToHTMLString(children)}**`
        },
        underline({ children }) {
          return `${serializeChildrenToHTMLString(children)}`
        },
        subscript({ children }) {
          return `${serializeChildrenToHTMLString(children)}`
        },
        superscript({ children }) {
          return `${serializeChildrenToHTMLString(children)}`
        },
        link({ mark, children }) {
          const href = String(mark.attrs.href || '');

          // Rewrite internal app URLs to Fumadocs paths
          // Examples to match:
          // /workspaces/<workspaceSlug>/docs/<topicSlug>?docId=<docId>&topicId=<topicId>
          // /<workspaceTitle>/<topicSlug>?docId=<docId>&topicId=<topicId>
          try {
            const isInternal = href.startsWith('/') && !href.startsWith('//');
            if (isInternal) {
              const url = new URL(href, 'http://localhost:3000'); // base required for parsing
              const pathname = url.pathname;

              // Case 1: /workspaces/<ws>/docs/<topic>
              const wsMatch = pathname.match(/^\/workspaces\/([^/]+)\/docs\/([^/]+)$/);
              if (wsMatch) {
                const canonicalSlug = wsMatch[2];
                return `[${serializeChildrenToHTMLString(children)}](/docs/${canonicalSlug})`;
              }

              // Case 2: /<workspaceTitle>/<topicSlug>
              const parts = pathname.split('/').filter(Boolean);
              if (parts.length >= 2) {
                const maybeTopic = parts[1];
                // assume second segment is topic canonical slug
                return `[${serializeChildrenToHTMLString(children)}](/docs/${maybeTopic})`;
              }
            }
          } catch {}

          return `[${serializeChildrenToHTMLString(children)}](${href})`
        },
        highlight({ children }) {
          return `**${serializeChildrenToHTMLString(children)}**`
        },
        comment({ children }) {
          // Comments should not appear in markdown output for Git
          // Return the content without any comment markup
          return serializeChildrenToHTMLString(children)
        },
        ...options?.markMapping,
      },
      ...options,
    },
  });
  
  // Debug log to check if undefined appears in the result
  if (htmlResult && htmlResult.includes('undefined')) {
    console.error('[renderToMarkdownReadable] WARNING: Result contains "undefined"!');
    console.error('[renderToMarkdownReadable] Content type:', content?.type);
    console.error('[renderToMarkdownReadable] Result preview:', htmlResult.substring(0, 500));
  }
  
  return htmlResult;
}

/**
 * Post-process MDX content to ensure proper formatting
 * Fixes common issues with HTML tags and component placement
 */
export function postProcessMDXContent(content: string): string {
  let processed = content;
  
  // Fix issue where closing </div> tags are immediately followed by content
  // This ensures block-level elements have proper line breaks
  processed = processed.replace(/(<\/div>)([^\n])/g, '$1\n$2');
  
  // Ensure MDX components after HTML blocks have proper spacing
  processed = processed.replace(/(<\/div>)\n(<[A-Z])/g, '$1\n\n$2');
  
  // Fix inline images that immediately follow closing divs
  processed = processed.replace(/(<\/div>)(!\[)/g, '$1\n\n$2');
  
  // Ensure proper spacing between adjacent MDX components
  processed = processed.replace(/(\/>)(<[A-Z])/g, '$1\n\n$2');
  
  // Fix columns layout specific issues
  // Ensure column content has proper spacing
  processed = processed.replace(/<div data-position="([^"]*)" data-type="column">\n([^\n])/g, 
    '<div data-position="$1" data-type="column">\n\n$2');
  
  // Clean up excessive blank lines (more than 2 consecutive)
  processed = processed.replace(/\n{3,}/g, '\n\n');
  
  // Ensure file ends with a single newline
  processed = processed.replace(/\n*$/, '\n');
  
  return processed;
}

/**
 * This code is just to show the flexibility of this renderer. We can potentially render content to any format we want.
 * This is a simple example of how we can render content to markdown. This is not a full implementation of a markdown renderer.
 */
export function renderToMarkdown(content: Node | JSONContent,
  extensions: Extensions = [],
  options: Partial<TiptapStaticRendererOptions<string, Mark, Node>> = {}
) {
  if (!content) {
    return ''
  }
  
  // Handle empty array content
  if (Array.isArray(content) && content.length === 0) {
    return ''
  }
  
  // Handle empty doc
  if (typeof content === 'object' && content.type === 'doc' && 
      (!content.content || (Array.isArray(content.content) && content.content.length === 0))) {
    return ''
  }
  
  if ((content.type === 'doc' || !content.type) && Array.isArray(content.content)) {
    let markdown = ''
    for (const child of (content.content ?? [])) {
      markdown += renderToMarkdown(child, extensions, options)
    }
    // Apply post-processing to ensure proper MDX formatting
    return postProcessMDXContent(markdown)
  }
  const result = renderToHTMLString({
    content,
    extensions: [
      ...ServerRenderExtensions,
      ...extensions
    ] as any,
    options: {
      nodeMapping: {
        // Remove custom text handler to allow marks to be processed
        bulletList({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}`
        },
        orderedList({ children }) {
          return `\n${serializeChildrenToHTMLString(children)}`
        },
        listItem({ node, children, parent }) {
          
          const id = node.attrs?.id;
          const rawContent = serializeChildrenToHTMLString(children).trim();
          const childrenContent = indentNestedLists(rawContent);
          
          if (parent?.type.name === 'bulletList') {
            if (id && id !== null) {
              return `- ${childrenContent} :dg{id="${id}"}\n`
            }
            return `- ${childrenContent}\n`
          }
          if (parent?.type.name === 'orderedList') {
            let number = parent.attrs.start || 1

            parent.forEach((parentChild, _offset, index) => {
              if (node === parentChild) {
                number = index + 1
              }
            })

            if (id && id !== null) {
              return `${number}. ${childrenContent} :dg{id="${id}"}\n`
            }
            return `${number}. ${childrenContent}\n`
          }

          return serializeChildrenToHTMLString(children)
        },
        // paragraph({ children }) {
        //   return `\n${serializeChildrenToHTMLString(children)}\n`
        // },
        paragraph(props) {
          const { node, children, parent } = props;
          const id = node.attrs?.id;
          
          // Check if this paragraph is inside a list item
          const isInListItem = parent?.type.name === 'listItem';
          
          // Check if paragraph contains inline components that shouldn't have :dg directives
          // These components handle their own rendering and shouldn't inherit paragraph IDs
          const childrenStr = serializeChildrenToHTMLString(children);
          const hasInlineComponent = childrenStr.includes('<Citation') || 
                                     childrenStr.includes('<Glossary') ||
                                     childrenStr.includes('<InlineButton') ||
                                     childrenStr.includes('<InlineImage') ||
                                     childrenStr.includes('<Emoji');
          
          if (id && id !== null && !hasInlineComponent) {
            if (isInListItem) {
              // For list items, put directive inline with content
              return `${childrenStr} :dg{id="${id}"}`;
            } else {
              // For regular paragraphs, put directive at the end of the line
              return `\n${childrenStr} :dg{id="${id}"}\n`;
            }
          }
          
          if (isInListItem) {
            // For list items, return content without extra newlines
            return childrenStr;
          }
          
          return `\n${childrenStr}\n`;
        },
        heading({ node, children }) {
          const level = node.attrs.level as number
          const id = node.attrs?.id;
          const content = serializeChildrenToHTMLString(children).trim();
          if (id && id !== null) {
            return `\n${new Array(level).fill('#').join('')} ${content} :dg{id="${id}"}\n\n`
          }
          return `\n${new Array(level).fill('#').join('')} ${content}\n\n`
        },
        codeBlock({ node, children }) {
          const language = normalizeCodeBlockLanguage(node.attrs?.language);
          return `\n\`\`\`${language}\n${serializeChildrenToHTMLString(children)}\n\`\`\`\n`
        },
        blockquote({ node, children }) {
          const id = node.attrs?.id;
          const blockquoteContent = serializeChildrenToHTMLString(children)
            .trim()
            .split('\n')
            .map(a => `> ${a}`)
            .join('\n');
          
          if (id && id !== null) {
            return `\n${blockquoteContent} :dg{id="${id}"}\n`;
          }
          return `\n${blockquoteContent}\n`;
        },
        image({ node }) {
          return `![${node.attrs.alt}](${node.attrs.src})`
        },
        hardBreak() {
          return '\n'
        },
        horizontalRule() {
          return '\n---\n'
        },
        templatePlaceholder({ node }) {
          return `{{${node.attrs?.name || 'PLACEHOLDER'}}}`
        },
        table({ children, node }) {
          if (!Array.isArray(children)) {
            return `\n${serializeChildrenToHTMLString(children)}\n`
          }

          // Compute number of columns from the first row's cell count
          const firstRowNode = (node as any)?.firstChild;
          const numCols = firstRowNode ? firstRowNode.childCount : 1;

          const header = serializeChildrenToHTMLString(children[0]);
          const separator = `| ${Array.from({ length: numCols }).map(() => '---').join(' | ')} |`;
          const body = serializeChildrenToHTMLString(children.slice(1));

          return `\n${header}${separator}\n${body}\n`;
        },
        tableRow({ children }) {
          if (Array.isArray(children)) {
            return `| ${children.join(' | ')} |\n`
          }
          return `${serializeChildrenToHTMLString(children)}\n`
        },
        tableHeader({ children }) {
          return serializeChildrenToHTMLString(children).trim()
        },
        tableCell({ children }) {
          return serializeChildrenToHTMLString(children).trim()
        },
        Card({ node, children }) {
          const attrs = node.attrs || {};
          // console.log('[Card] Original attrs:', JSON.stringify(attrs));
          const processedAttrs = transformCustomAttrs(attrs);
          // console.log('[Card] Processed attrs:', JSON.stringify(processedAttrs));
          
          const entries = Object.entries(processedAttrs);
          // console.log('[Card] Entries before filter:', entries);
          const filtered = entries.filter(([_key, value]) => value !== undefined && value !== null);
          // console.log('[Card] Entries after filter:', filtered);
          
          const props = filtered
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Card${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Card>\n` : `${open} />`;
        },

        Step({ node, children }) {
          const attrs = node.attrs || {};
          // console.log('[Step] Original attrs:', JSON.stringify(attrs));
          const processedAttrs = transformCustomAttrs(attrs);
          // console.log('[Step] Processed attrs:', JSON.stringify(processedAttrs));
          
          // Add screenshot-related attributes if they exist
          if (attrs.screenshotUrl) {
            processedAttrs['screenshotUrl'] = attrs.screenshotUrl;
          }
          if (attrs.screenshotWidth) {
            processedAttrs['screenshotWidth'] = attrs.screenshotWidth;
          }
          if (attrs.screenshotHeight) {
            processedAttrs['screenshotHeight'] = attrs.screenshotHeight;
          }
          if (attrs.screenshotZoomLevel) {
            processedAttrs['screenshotZoomLevel'] = attrs.screenshotZoomLevel;
          }
          if (attrs.screenshotPanX !== undefined) {
            processedAttrs['screenshotPanX'] = attrs.screenshotPanX;
          }
          if (attrs.screenshotPanY !== undefined) {
            processedAttrs['screenshotPanY'] = attrs.screenshotPanY;
          }
          if (attrs.cropStyle) {
            processedAttrs['cropStyle'] = attrs.cropStyle;
          }
          if (attrs.align) {
            processedAttrs['align'] = attrs.align;
          }
          if (attrs.interactionPoints) {
            processedAttrs['interactionPoints'] = attrs.interactionPoints;
          }
          if (attrs.elementBox) {
            processedAttrs['elementBox'] = attrs.elementBox;
          }
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Step${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Step>\n` : `${open} />`;
        },

        Tab({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Tab${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Tab>\n` : `${open} />`;
        },

        Tabs({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          const props = Object.entries(processedAttrs)
            .filter(([_k, v]) => v !== undefined && v !== null)
            .map(([k, v]) => serializeAttr(k, v))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(l => '  ' + l).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Tabs${props ? ' ' + props : ''}`;
          return inner ? `${open}>\n${inner}\n\n</Tabs>\n` : `${open} />`;
        },

        /* --- Task List Items --- */
        // Previous HTML-based implementation preserved for reference:
        // taskItem({ node, children }) {
        //   const checked = !!node.attrs?.checked;
        //   const dataCheckedAttr = `data-checked="${checked ? 'true' : 'false'}"`;
        //   const inputTag = checked
        //     ? '<input type="checkbox" checked="checked"/>'
        //     : '<input type="checkbox" checked={false}/>';
        //   const contentHtml = serializeChildrenToHTMLString(children);
        //   return `\n<li ${dataCheckedAttr} data-type="taskItem"><label>${inputTag}<span/></label><div>\n${contentHtml}\n</div></li>`;
        // },
        // taskList({ children }) {
        //   return `\n<ul data-type=\"taskList\">${serializeChildrenToHTMLString(children)}</ul>`;
        // },
        taskItem({ node, children }) {
          const checked = !!node.attrs?.checked;
          const content = serializeChildrenToHTMLString(children).trim();
          return `- [${checked ? 'x' : ' '}] ${content}\n`;
        },

        taskList({ children }) {
          // Simply concatenate items; each taskItem already includes trailing newline
          return `\n${serializeChildrenToHTMLString(children)}`;
        },

        /* --- Image Upload (custom) --- */
        imageUpload({ node }) {
          const attrs = node.attrs || {};
          const processedAttrs = { ...attrs };
          if (!processedAttrs['data-type']) {
            processedAttrs['data-type'] = 'image-upload';
          }
          const props = Object.entries(processedAttrs)
            .filter(([_k, v]) => v !== undefined && v !== null)
            .map(([k, v]) => serializeAttr(k, v))
            .join(' ');
          return `\n<div ${props} />`;
        },

        

        // Internal notes are editor-only and should not be published (MDX export)
        InternalNote() {
          return ''
        },

        Screenshot({ node }) {
          // Note: Screenshot is deprecated - screenshots are now integrated into Step elements via cropStyle
          // This handler is kept for backward compatibility with existing content
          const attrs = node.attrs || {};
          
          // Handle special cases for object attributes
          const processedAttrs = transformCustomAttrs(attrs);
          if (attrs.step_id) {
            processedAttrs.stepId = attrs.step_id;
            delete processedAttrs.step_id;
          }
          if (attrs.interactionPoints) {
            // Base64 encode to avoid escaping issues in MDX
            const jsonStr = JSON.stringify(attrs.interactionPoints);
            processedAttrs.interactionPoints = encodeBase64(jsonStr);
            if (processedAttrs.interactionPoints) {
              processedAttrs['data-interaction-points-encoded'] = 'base64';
            }
          }
          if (attrs.elementBox) {
            // Base64 encode to avoid escaping issues in MDX
            const jsonStr = JSON.stringify(attrs.elementBox);
            processedAttrs.elementBox = encodeBase64(jsonStr);
            if (processedAttrs.elementBox) {
              processedAttrs['data-element-box-encoded'] = 'base64';
            }
          }
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          return `<Screenshot${props ? ' ' + props : ''} />`;
        },

        Admonition({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Admonition${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Admonition>\n` : `${open} />`;
        },

        Accordion({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Accordion${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Accordion>\n` : `${open} />`;
        },

        AccordionGroup({ node: _node, children }) {
          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          return `\n<AccordionGroup>\n${inner}\n\n</AccordionGroup>\n`;
        },

        Citation({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          const isSingleWorkspacePortal = Boolean((options as any)?.singleWorkspacePortal);
          // console.log("isSingleWorkspacePortal", isSingleWorkspacePortal)
          // When portal has only one workspace, set showWorksapceSlug to false; otherwise true
          processedAttrs.showWorksapceSlug = !isSingleWorkspacePortal;

          // Extract and explicitly emit showWorksapceSlug as {true|false}
          const show = processedAttrs.showWorksapceSlug;
          delete processedAttrs.showWorksapceSlug;

          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          const explicit = show === undefined ? '' : ` showWorksapceSlug={${show ? true : false}}`;
          const inner = serializeChildrenToHTMLString(children);
          return `<Citation${props ? ' ' + props : ''}${explicit}>${inner}</Citation>`;
        },

        Glossary({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          const inner = serializeChildrenToHTMLString(children);
          return `<Glossary${props ? ' ' + props : ''}>${inner}</Glossary>`;
        },

        FolderContents({ node }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          return `<FolderContents${props ? ' ' + props : ''} />`;
        },

        CardGroup({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<CardGroup${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</CardGroup>\n` : `${open} />`;
        },

        Steps({ children }) {
          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          return `\n<Steps>\n${inner}\n\n</Steps>\n`;
        },
        toggle({ node, children }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          let inner = serializeChildrenToHTMLString(children).split('\n').map(line => '  ' + line).join('\n');
          inner = inner.trimEnd();
          const open = `\n<Toggle${props ? ' ' + props : ''}`;

          return inner ? `${open}>\n${inner}\n\n</Toggle>\n` : `${open} />`;
        },
        mermaid({ node, children }) {
          // Get code from content text or attrs as fallback
          const codeFromContent = children ? serializeChildrenToHTMLString(children) : ''
          const codeFromAttrs = node.attrs?.code || ''
          const code = codeFromContent || codeFromAttrs

          // Export as MDX component instead of markdown code block
          return `\n<Mermaid>\n{\`${code}\`}\n</Mermaid>\n`
        },

        youtube({ node }) {
          const attrs = node.attrs || {};
          const src = attrs.src || '';

          // Extract only the attributes that have defined values
          const definedAttrs: Record<string, any> = {};

          // Always include src
          if (src) {
            definedAttrs.src = src;
          }

          // Only include other attributes if they have real values (not undefined/null)
          if (attrs.width !== undefined && attrs.width !== null) {
            definedAttrs.width = attrs.width;
          }
          if (attrs.height !== undefined && attrs.height !== null) {
            definedAttrs.height = attrs.height;
          }
          if (attrs.start !== undefined && attrs.start !== null && attrs.start !== 0) {
            definedAttrs.start = attrs.start;
          }

          const props = Object.entries(definedAttrs)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          return `\n<YouTube${props ? ' ' + props : ''} />\n`;
        },

        inlineButton({ node }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          // rewrite href if internal
          if (processedAttrs.href && typeof processedAttrs.href === 'string') {
            try {
              const href = processedAttrs.href as string;
              if (href.startsWith('/') && !href.startsWith('//')) {
                const url = new URL(href, 'http://local');
                const segs = url.pathname.split('/').filter(Boolean);
                const wsIdx = segs.findIndex(s => s === 'workspaces');
                const docsIdx = segs.findIndex(s => s === 'docs');
                if (wsIdx >= 0 && docsIdx >= 0 && wsIdx + 1 < segs.length && docsIdx + 1 < segs.length && docsIdx > wsIdx) {
                  const ws = segs[wsIdx + 1];
                  processedAttrs.href = `/docs/${ws}/${segs.slice(docsIdx + 1).join('/')}`;
                } else if (segs.length >= 2) {
                  const ws = segs[0];
                  processedAttrs.href = `/docs/${ws}/${segs.slice(1).join('/')}`;
                }
              }
            } catch {}
          }
          
          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          return `<InlineButton${props ? ' ' + props : ''} />`;
        },

        actionPill({ node }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);
          const props: string[] = [];

          for (const [key, value] of Object.entries(processedAttrs)) {
            if (value === undefined || value === null || value === '') continue;

            if (key === 'params') {
              const serializedParams = serializeActionPillParams(value);
              if (serializedParams) {
                props.push(serializedParams);
              }
              continue;
            }

            props.push(serializeAttr(key, value));
          }

          const serializedProps = props.join(' ');
          return `<ActionPill${serializedProps ? ' ' + serializedProps : ''} />`;
        },

        runbookRefMention({ node }) {
          const ref = node.attrs?.ref;
          return typeof ref === 'string' && ref.length > 0 ? ref : '';
        },

        inlineImage({ node }) {
          const attrs = node.attrs || {};
          const processedAttrs = transformCustomAttrs(attrs);

          const props = Object.entries(processedAttrs)
            .filter(([_key, value]) => value !== undefined && value !== null)
            .map(([key, value]) => serializeAttr(key, value))
            .join(' ');

          return `<InlineImage${props ? ' ' + props : ''} />`;
        },

        emoji({ node }) {
          const attrs = node.attrs || {};
          // Prefer explicit char; otherwise try to resolve by name; fallback to empty string
          let char = attrs.char as string | undefined;
          if (!char && attrs.name) {
            const emojiData = gitHubEmojis.find(e => e.name === attrs.name);
            if (emojiData && emojiData.emoji) {
              char = emojiData.emoji;
            }
          }
          return char || '';
        },

        // Columns layout -> emit JSX-safe className
        columns({ node, children }) {
          const layout = node.attrs?.layout || 'two-column'
          const header = `<div data-type="columns" className="layout-${layout}">`
          const inner = serializeChildrenToHTMLString(children)
          // Ensure proper line breaks after closing div
          return `\n${header}${inner}\n</div>\n`
        },

        column({ node, children }) {
          const position = node.attrs?.position || node.attrs?.['data-position'] || ''
          const header = `<div data-position="${position}" data-type="column">`
          const inner = serializeChildrenToHTMLString(children)
          // Ensure proper line breaks around column content
          return `${header}\n\n${inner}\n\n</div>`
        },

        ...options?.nodeMapping,
      },
      markMapping: {
        bold({ children }) {
          return `**${serializeChildrenToHTMLString(children)}**`
        },
        italic({ children, node }) {
          let isBoldToo = false

          // Check if the node being wrapped also has a bold mark, if so, we need to use the bold markdown syntax
          if (node?.marks.some(m => m.type.name === 'bold')) {
            isBoldToo = true
          }

          if (isBoldToo) {
            // If the content is bold, just wrap the bold content in italic markdown syntax with another set of asterisks
            return `*${serializeChildrenToHTMLString(children)}*`
          }

          return `_${serializeChildrenToHTMLString(children)}_`
        },
        code({ children }) {
          return `\`${serializeChildrenToHTMLString(children)}\``
        },
        strike({ children }) {
          return `~~${serializeChildrenToHTMLString(children)}~~`
        },
        underline({ children }) {
          return `<u>${serializeChildrenToHTMLString(children)}</u>`
        },
        subscript({ children }) {
          return `<sub>${serializeChildrenToHTMLString(children)}</sub>`
        },
        superscript({ children }) {
          return `<sup>${serializeChildrenToHTMLString(children)}</sup>`
        },
        link({ mark, children }) {
          const isSingleWorkspacePortal = Boolean((options as any)?.singleWorkspacePortal);
          const href = String(mark.attrs.href || '');
          const target = mark.attrs.target;

          // Rewrite internal app URLs to Fumadocs paths including workspace
          try {
            const isInternal = href.startsWith('/') && !href.startsWith('//');
            if (isInternal) {
              const url = new URL(href, 'http://local');
              const segments = url.pathname.split('/').filter(Boolean);
              // /workspaces/<ws>/docs/<tail...>
              const wsIdx = segments.findIndex(s => s === 'workspaces');
              const docsIdx = segments.findIndex(s => s === 'docs');
              if (wsIdx >= 0 && docsIdx >= 0 && wsIdx + 1 < segments.length && docsIdx + 1 < segments.length && docsIdx > wsIdx) {
                const ws = segments[wsIdx + 1];
                const tail = segments.slice(docsIdx + 1); // [parent, child, ...]
                const mdHref = isSingleWorkspacePortal ? `/docs/${tail.join('/')}` : `/docs/${ws}/${tail.join('/')}`;
                return target
                  ? `[${serializeChildrenToHTMLString(children)}](${mdHref} "${target}")`
                  : `[${serializeChildrenToHTMLString(children)}](${mdHref})`;
              }
              // /<ws>/<tail...>
              if (segments.length >= 2) {
                const ws = segments[0];
                const tail = segments.slice(1);
                const mdHref = isSingleWorkspacePortal ? `/docs/${tail.join('/')}` : `/docs/${ws}/${tail.join('/')}`;
                return target
                  ? `[${serializeChildrenToHTMLString(children)}](${mdHref} "${target}")`
                  : `[${serializeChildrenToHTMLString(children)}](${mdHref})`;
              }
            }
          } catch {}

          return target
            ? `[${serializeChildrenToHTMLString(children)}](${href} "${target}")`
            : `[${serializeChildrenToHTMLString(children)}](${href})`;
        },
        highlight({ children }) {
          return `==${serializeChildrenToHTMLString(children)}==`
        },
        comment({ children }) {
          // Comments should not appear in markdown output for Git
          // Return the content without any comment markup
          return serializeChildrenToHTMLString(children)
        },
        ...options?.markMapping,
      },
      ...options,
    },
  });
  
  // Debug log to check if undefined appears in the result
  if (result && result.includes('undefined')) {
    console.error('[renderToMarkdown] WARNING: Result contains "undefined"!');
    console.error('[renderToMarkdown] Content type:', content?.type);
    console.error('[renderToMarkdown] Result preview:', result.substring(0, 500));
    // Try to find where undefined comes from
    const undefinedIndex = result.indexOf('undefined');
    console.error('[renderToMarkdown] Context around undefined:', result.substring(Math.max(0, undefinedIndex - 50), Math.min(result.length, undefinedIndex + 50)));
  }
  
  return result;
}
